import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { readGiftConfig, giftThresholds } from '@theme/gift-rules';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  
  /** @returns {import('./gift-rules').GiftConfig | null} */
  get #giftConfig() {
    return readGiftConfig(this);
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    const r = this.refs.cartItemRows;
    return /** @type {HTMLElement[]} */ (Array.isArray(r) ? r : r ? [r] : []);
  }

  /** @returns {HTMLElement[]} */
  get #quantitySelectors() {
    const r = this.refs.quantitySelectors;
    return /** @type {HTMLElement[]} */ (Array.isArray(r) ? r : r ? [r] : []);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /** @param {CustomEvent} event */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      this.onLineItemRemove(line);
    } else {
      if (this.#shouldShowGiftNotification(line, quantity)) {
        this.#showGiftToast();
      }
      this.updateQuantity({ line, quantity, action: 'change' });
      const lineItemRow = this.#rows.find(row => {
        const input = row.querySelector('input[data-cart-line]');
        return input && parseInt(input.getAttribute('data-cart-line') || '0') === line;
      });
      if (!lineItemRow) return;
      const textComponent = /** @type {any} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }
  }

  /**
   * @param {number} lineIndex
   * @param {number} newQty
   */
  #shouldShowGiftNotification(lineIndex, newQty) {
    const config = this.#giftConfig;
    if (!config) return false;

    const thresholds = giftThresholds(config);
    if (thresholds.length === 0) return false;

    const rows = this.#rows;
    // Find by data-cart-line instead of DOM position — two-pass rendering reorders DOM
    // but data-cart-line always reflects the true Shopify cart line index
    const targetRow = rows.find(row => {
      const input = row.querySelector('input[data-cart-line]');
      return input && parseInt(input.getAttribute('data-cart-line') || '0') === lineIndex;
    });
    if (!targetRow) return false;

    const isMainRow = (/** @type {HTMLElement} */ row) =>
      String(row.dataset.productId || '') === String(config.mainProductId);

    if (!isMainRow(targetRow)) return false;

    const oldQty = parseInt(targetRow.querySelector('input')?.getAttribute('data-cart-quantity') || '0');
    if (newQty <= oldQty) return false;

    let totalOld = 0;
    for (const row of rows) {
      if (isMainRow(row)) {
        totalOld += parseInt(row.querySelector('input')?.getAttribute('data-cart-quantity') || '0');
      }
    }
    const totalNew = totalOld - oldQty + newQty;

    return thresholds.some(threshold => totalOld < threshold && totalNew >= threshold);
  }

  #showGiftToast() {
    const scrollHint = this.querySelector('scroll-hint.cart-drawer__items');
    const fallback = this.querySelector('.cart-drawer__summary');
    if (!scrollHint && !fallback) return;
    let toast = /** @type {HTMLElement|null} */ (this.querySelector('#cart-gift-toast'));
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-gift-toast';
      toast.innerHTML = `<span class="cart-gift-toast__icon">🎁</span><span class="cart-gift-toast__text">Your free gift is being added<span class="cart-gift-toast__dots"><span></span><span></span><span></span></span></span>`;
      if (scrollHint) {
        scrollHint.insertAdjacentElement('beforeend', toast);
      } else {
        fallback.insertAdjacentElement('beforebegin', toast);
      }
    }
    toast.style.display = 'flex';
    requestAnimationFrame(() => requestAnimationFrame(() => /** @type {HTMLElement} */ (toast).classList.add('visible')));
  }

  #hideGiftToast() {
    const toast = /** @type {HTMLElement|null} */ (this.querySelector('#cart-gift-toast'));
    if (!toast) return;
    toast.classList.remove('visible');
    toast.addEventListener('transitionend', () => { /** @type {HTMLElement} */ (toast).style.display = 'none'; }, { once: true });
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
    const cartItemRowToRemove = this.#rows.find(row => {
      const input = row.querySelector('input[data-cart-line]');
      return input && parseInt(input.getAttribute('data-cart-line') || '0') === line;
    });
    if (!cartItemRowToRemove) return;
    const rowsToRemove = [cartItemRowToRemove, ...this.#rows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key)];
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Main quantity update function, now async to handle gift logic sequentially.
   * @param {{line: number, quantity: number, action: string}} config
   */
  async updateQuantity(config) {
    const cartPerformanceMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    const cartTotal = /** @type {any} */ (this.refs.cartTotal);

    try {
      // Step 1: Update the primary item's quantity
      const sectionsToUpdate = new Set([this.sectionId, ...Array.from(document.querySelectorAll('cart-items-component')).map(el => /** @type {HTMLElement} */ (el).dataset.sectionId).filter(Boolean)]);
      const body = JSON.stringify({
        line: config.line,
        quantity: config.quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();
      
      const initialResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      let cartAfterPrimaryUpdate = await initialResponse.json();

      if (cartAfterPrimaryUpdate.errors) {
        this.#handleCartError(config.line, cartAfterPrimaryUpdate);
        return; // Stop processing if the initial update failed
      }
      
      // Step 2: Check for and apply gift updates based on the new cart state
      const cartAfterGiftUpdate = await this.#checkAndManageGifts(cartAfterPrimaryUpdate);
      const finalCartState = cartAfterGiftUpdate || cartAfterPrimaryUpdate; // Use the result from gift logic if it ran

      // Step 3: Render the final state of the cart
      resetShimmer(this);

      // We use the final state to update the UI
      this.#updateQuantitySelectors(finalCartState);

      this.dispatchEvent(
        new CartUpdateEvent(finalCartState, this.sectionId, {
          itemCount: finalCartState.item_count,
          source: 'cart-items-component',
          sections: finalCartState.sections,
        })
      );

      // Morph the section using the final cart state's HTML
      if (finalCartState.sections && finalCartState.sections[this.sectionId]) {
        morphSection(this.sectionId, finalCartState.sections[this.sectionId]);
      }

      this.#hideGiftToast();
      this.#updateCartQuantitySelectorButtonStates();

    } catch (error) {
      console.error('CartItems updateQuantity Error:', error);
      this.#hideGiftToast();
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceMarker);
    }
  }

  /**
   * Hands the gift reconciliation to gift-manager-component, which owns the
   * rules and the cart write. This used to be a second copy of that logic
   * living here, which meant every rule change had to be made twice.
   *
   * @param {any} cartData the cart as it stands after the customer's own change
   * @returns {Promise<any|null>} the updated cart, or null when nothing changed
   */
  async #checkAndManageGifts(cartData) {
    if (!this.#giftConfig) return null;

    const manager = /** @type {any} */ (document.querySelector('gift-manager-component'));
    if (!manager || typeof manager.checkAndManageGifts !== 'function') return null;

    // Silent, because this component announces the cart update itself once it
    // has the final state. Letting the manager announce too renders twice.
    return manager.checkAndManageGifts(cartData, { announce: false });
  }

  handleDiscountUpdate = (/** @type {any} */ event) => this.#handleCartUpdate(event);

  #handleCartError = (/** @type {number} */ line, /** @type {any} */ parsedResponseText) => {
    const quantitySelector = this.#quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  #handleCartUpdate = (/** @type {any} */ event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems = () => this.classList.add('cart-items-disabled');
  #enableCartItems = () => this.classList.remove('cart-items-disabled');

  /** @param {any} updatedCart */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      (/** @type {any} */ (selector)).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
